/**
 * Shared modal primitives (audit a11y pass).
 *
 * The app accumulated six hand-rolled createPortal overlays — none with
 * role="dialog"/aria-modal, Escape handling, or focus management — plus five
 * native confirm()/prompt() calls that broke the visual language entirely.
 * This is the one implementation they all use:
 *
 * - <Dialog>: portal + overlay, role="dialog" aria-modal, overlay-click and
 *   Escape to close (suppressed while `busy`), focus moves into the panel on
 *   open, is trapped on Tab, and returns to the opener on close.
 * - <ConfirmDialog>: the confirm() replacement — message + cancel/confirm.
 * - <PromptDialog>: the prompt() replacement — one text input.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { cn } from '@/lib/cn';
import { Button } from './Button';
import { Input, Label } from './Input';

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export interface DialogProps {
  open: boolean;
  onClose: () => void;
  /** Header content; omit for a fully custom panel (no header row). */
  title?: ReactNode;
  /** While true, overlay/Escape/the X won't close (mutation in flight). */
  busy?: boolean;
  /** Extra classes for the panel (e.g. max-w overrides). */
  className?: string;
  children: ReactNode;
}

export function Dialog({ open, onClose, title, busy = false, className, children }: DialogProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  // Refs so the open-scoped effect never has to re-run mid-dialog.
  const busyRef = useRef(busy);
  busyRef.current = busy;
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return;
    const opener = document.activeElement as HTMLElement | null;
    const panel = panelRef.current;
    const first = panel?.querySelector<HTMLElement>(FOCUSABLE);
    (first ?? panel)?.focus();

    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        if (!busyRef.current) {
          e.stopPropagation();
          onCloseRef.current();
        }
        return;
      }
      if (e.key === 'Tab' && panel) {
        const focusables = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE));
        if (focusables.length === 0) return;
        const firstEl = focusables[0]!;
        const lastEl = focusables[focusables.length - 1]!;
        const active = document.activeElement;
        if (e.shiftKey && (active === firstEl || active === panel)) {
          e.preventDefault();
          lastEl.focus();
        } else if (!e.shiftKey && active === lastEl) {
          e.preventDefault();
          firstEl.focus();
        }
      }
    }
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      opener?.focus?.();
    };
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40"
      onClick={() => !busy && onClose()}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === 'string' ? title : undefined}
        tabIndex={-1}
        className={cn(
          'bg-bg rounded-xl border border-line shadow-xl w-full max-w-md max-h-[90vh] overflow-auto outline-none',
          className,
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {title != null && (
          <div className="flex items-center justify-between p-4 border-b border-line">
            <div className="flex items-center gap-2 font-semibold text-sm">{title}</div>
            <button
              type="button"
              aria-label="Close"
              onClick={() => !busy && onClose()}
              className="text-ink-faint hover:text-ink"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}
        {children}
      </div>
    </div>,
    document.body,
  );
}

export interface ConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: ReactNode;
  /** The question / consequence text. */
  body: ReactNode;
  confirmLabel?: string;
  /** 'danger' renders the confirm button destructive (deletes etc.). */
  tone?: 'default' | 'danger';
  busy?: boolean;
}

export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  body,
  confirmLabel = 'Confirm',
  tone = 'default',
  busy = false,
}: ConfirmDialogProps) {
  return (
    <Dialog open={open} onClose={onClose} title={title} busy={busy}>
      <div className="p-4 space-y-4">
        <div className="text-sm text-ink-dim whitespace-pre-wrap">{body}</div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            size="sm"
            variant={tone === 'danger' ? 'destructive' : 'default'}
            onClick={onConfirm}
            disabled={busy}
          >
            {confirmLabel}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

export interface PromptDialogProps {
  open: boolean;
  onClose: () => void;
  /** Called with the trimmed value ('' allowed — caller decides semantics). */
  onSubmit: (value: string) => void;
  title: ReactNode;
  label: ReactNode;
  placeholder?: string;
  hint?: ReactNode;
  submitLabel?: string;
  inputType?: string;
  busy?: boolean;
}

export function PromptDialog({
  open,
  onClose,
  onSubmit,
  title,
  label,
  placeholder,
  hint,
  submitLabel = 'OK',
  inputType = 'text',
  busy = false,
}: PromptDialogProps) {
  const [value, setValue] = useState('');
  useEffect(() => {
    if (open) setValue('');
  }, [open]);

  return (
    <Dialog open={open} onClose={onClose} title={title} busy={busy}>
      <form
        className="p-4 space-y-3"
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit(value.trim());
        }}
      >
        <div>
          <Label htmlFor="dialog-prompt-input">{label}</Label>
          <Input
            id="dialog-prompt-input"
            type={inputType}
            value={value}
            placeholder={placeholder}
            onChange={(e) => setValue(e.target.value)}
          />
          {hint && <p className="text-[11px] text-ink-faint mt-1">{hint}</p>}
        </div>
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" size="sm" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button type="submit" size="sm" disabled={busy}>
            {submitLabel}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
